"use client";

import Link from "next/link";
import Image from "next/image";

interface Campaign {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  orgName: string | null;
  coverImageUrl?: string;
  status: string;
  startAt: string | null;
  endAt: string | null;
  participantCount: number;
  clueCount: number;
}

interface FeaturedCampaignSectionProps {
  tenant: {
    slug: string;
    primaryColor: string;
    secondaryColor?: string;
    accentColor?: string;
  };
  campaign: Campaign | null;
  title?: string | null;
  description?: string | null;
}

/**
 * Featured Campaign Section Component for Tenant Pages
 *
 * Displays a highlighted campaign with a hero image, overlaid title and status,
 * stats, and call-to-action buttons. Uses tenant colors for styling.
 */
export function FeaturedCampaignSection({
  tenant,
  campaign,
  title,
  description,
}: FeaturedCampaignSectionProps) {
  if (!campaign) {
    return null;
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "PUBLISHED":
        return "bg-blue-500/90 text-white";
      case "ACTIVE":
        return "bg-green-500/90 text-white";
      case "ENDED":
        return "bg-red-500/90 text-white";
      case "ARCHIVED":
        return "bg-gray-500/90 text-white";
      default:
        return "bg-gray-500/90 text-white";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "PUBLISHED":
        return "Published";
      case "ACTIVE":
        return "Active";
      case "ENDED":
        return "Ended";
      case "ARCHIVED":
        return "Archived";
      default:
        return status;
    }
  };

  const isActive = () => {
    const now = new Date();
    const start = campaign.startAt ? new Date(campaign.startAt) : null;
    const end = campaign.endAt ? new Date(campaign.endAt) : null;

    return (
      campaign.status === "ACTIVE" ||
      (campaign.status === "PUBLISHED" &&
        (!start || start <= now) &&
        (!end || end >= now))
    );
  };

  const displayTitle = title || "Featured Hunt";

  return (
    <div className="max-w-4xl mx-auto mb-16">
      <div
        className="rounded-xl shadow-xl overflow-hidden"
        style={{ backgroundColor: 'var(--theme-bg, #FFFFFF)' }}
      >
        {/* Hero Image Section with Overlaid Text */}
        <div className="relative h-72 md:h-80">
          {/* Background Image or Gradient Fallback */}
          {campaign.coverImageUrl ? (
            <Image
              src={campaign.coverImageUrl}
              alt={campaign.title}
              fill
              className="object-cover"
              unoptimized
            />
          ) : (
            <div
              className="absolute inset-0"
              style={{
                background: `linear-gradient(135deg, ${tenant.primaryColor || '#3B82F6'} 0%, ${tenant.accentColor || '#8B5CF6'} 100%)`
              }}
            />
          )}

          {/* Gradient Overlay for Text Readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />

          {/* Overlaid Content */}
          <div className="absolute inset-0 flex flex-col justify-between p-6 md:p-8">
            {/* Top Row: Featured Hunt Label & Status */}
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <span
                  className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-full backdrop-blur-sm"
                  style={{
                    backgroundColor: `${tenant.secondaryColor || tenant.primaryColor || '#3B82F6'}CC`,
                    color: 'white'
                  }}
                >
                  {displayTitle}
                </span>
              </div>
              <span
                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-full backdrop-blur-sm ${getStatusColor(campaign.status)}`}
              >
                {getStatusLabel(campaign.status)}
              </span>
            </div>

            {/* Bottom Row: Campaign Title */}
            <div>
              <h3
                className="text-3xl md:text-4xl font-bold text-white mb-2"
                style={{
                  textShadow: '0 2px 4px rgba(0,0,0,0.5), 0 4px 12px rgba(0,0,0,0.3)'
                }}
              >
                {campaign.title}
              </h3>
              {campaign.orgName && (
                <p
                  className="text-white/90 text-sm md:text-base font-medium"
                  style={{ textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}
                >
                  Hosted by {campaign.orgName}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Content Below Image */}
        <div className="p-6 md:p-8">
          {/* Section Description (if provided) */}
          {description && (
            <p
              className="mb-4 text-sm"
              style={{ color: 'var(--theme-text, #6B7280)', opacity: 0.8 }}
            >
              {description}
            </p>
          )}

          {/* Campaign Description */}
          <p
            className="text-lg mb-6 whitespace-pre-line line-clamp-4"
            style={{ color: 'var(--theme-text, #4B5563)' }}
          >
            {campaign.description}
          </p>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            <div
              className="text-center p-4 rounded-lg"
              style={{ backgroundColor: 'var(--theme-bg, #F3F4F6)' }}
            >
              <div
                className="text-2xl font-bold"
                style={{ color: tenant.primaryColor || '#3B82F6' }}
              >
                {campaign.clueCount}
              </div>
              <div
                className="text-sm"
                style={{ color: 'var(--theme-text, #6B7280)' }}
              >
                Locations
              </div>
            </div>
            <div
              className="text-center p-4 rounded-lg"
              style={{ backgroundColor: 'var(--theme-bg, #F3F4F6)' }}
            >
              <div
                className="text-2xl font-bold"
                style={{ color: tenant.accentColor || '#10B981' }}
              >
                {campaign.participantCount}
              </div>
              <div
                className="text-sm"
                style={{ color: 'var(--theme-text, #6B7280)' }}
              >
                Participants
              </div>
            </div>
            <div
              className="text-center p-4 rounded-lg col-span-2 md:col-span-1"
              style={{ backgroundColor: 'var(--theme-bg, #F3F4F6)' }}
            >
              <div
                className="text-2xl font-bold truncate"
                style={{ color: 'var(--theme-secondary, #8B5CF6)' }}
              >
                {campaign.orgName || 'Organization'}
              </div>
              <div
                className="text-sm"
                style={{ color: 'var(--theme-text, #6B7280)' }}
              >
                Hosted by
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="flex justify-center">
            <Link
              href={`/${tenant.slug}/campaigns/${campaign.slug}`}
              className="px-8 py-3 rounded-lg font-semibold text-white transition-all hover:opacity-90 hover:scale-105 shadow-lg"
              style={{ backgroundColor: tenant.primaryColor || '#3B82F6' }}
            >
              {isActive() ? 'Start Hunt' : 'View Details'}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
